import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Mail, 
  CheckCircle2, 
  X, 
  Building2, 
  Loader2,
  Clock,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { formatOrgRoleLabel } from '../../lib/orgRoles';
import { API_URL } from '../../config';

const PendingInvites = ({ onInviteAccepted }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const fetchInvites = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/users/me/invites`);
      setInvites(response.data);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleAccept = async (inviteId) => {
    setProcessingId(inviteId);
    try {
      const response = await axios.post(`${API_URL}/users/me/invites/${inviteId}/accept`);
      toast.success(`You've joined ${response.data.organization_name}!`);
      setInvites(prev => prev.filter(inv => inv.id !== inviteId));
      if (onInviteAccepted) {
        onInviteAccepted(response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to accept invite';
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (inviteId) => {
    setProcessingId(inviteId);
    try {
      await axios.post(`${API_URL}/users/me/invites/${inviteId}/decline`);
      toast.success('Invite declined');
      setInvites(prev => prev.filter(inv => inv.id !== inviteId));
    } catch (error) {
      toast.error('Failed to decline invite');
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return null;
  }

  if (invites.length === 0) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800 mb-6" data-testid="pending-invites-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-[Manrope] text-slate-900 dark:text-white flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Pending Invitations
          <Badge className="bg-blue-600 text-white">{invites.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {invites.map((invite) => (
          <div
            key={invite.id}
            className="bg-white dark:bg-slate-800 rounded-lg p-4 flex items-center justify-between gap-4 shadow-sm"
            data-testid={`pending-invite-${invite.id}`}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-slate-900 dark:text-white truncate">
                  {invite.organization_name}
                </h4>
                <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                  <span>
                    Role:{' '}
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {formatOrgRoleLabel(invite.role)}
                    </span>
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <UserPlus className="w-3 h-3" />
                    {invite.invited_by}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                  <Clock className="w-3 h-3" />
                  Expires {new Date(invite.expires_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDecline(invite.id)}
                disabled={processingId === invite.id}
                className="text-slate-600 dark:text-slate-400"
                data-testid={`decline-invite-${invite.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                onClick={() => handleAccept(invite.id)}
                disabled={processingId === invite.id}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid={`accept-invite-${invite.id}`}
              >
                {processingId === invite.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Accept
                  </>
                )}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PendingInvites;
